import { useEffect, useMemo, useState } from 'react';

import { Icon } from '../../common/Icon/Icon';
import { buildParams } from '../../core/helpers';
import {
  AdhocAuthorisationPaymentMethod,
  Charger,
  DistinctConnectors,
  Roaming,
} from '../../core/models';
import AvailabilityChart from './AvailabilityChart';
import './ChargerDetails.css';
import ChargerEVSEInfo from './ChargerEVSEInfo';

interface Props {
  item: Charger;
  atProperty?: boolean;
}

function ChargerDetails({ item, atProperty }: Props) {
  const [chargerInfo, setChargerInfo] = useState<Charger | null>(null);

  useEffect(() => {
    const isSubscribed = true;

    const fetchChargerInfo = async (params: { charger_id: string }) => {
      const result = await fetch(
        `${process.env.REACT_APP_SC_API_BASE}/get_charger_info?${buildParams(params)}`,
      );
      const resData = await result.json();

      if (isSubscribed) {
        setChargerInfo(resData);
      }
    };

    fetchChargerInfo({ charger_id: item.id }).catch(console.error);
  }, [item.id]);

  const checkinWeekday = -1;

  let openingTimes = 'Open 24 hours, 7 days a week';
  if (item.opening_times) {
    if (item.opening_times.twentyfourseven) {
      // this is default
    } else if (item.opening_times.Text) {
      openingTimes = item.opening_times.Text;
    }
  }

  let reliability = null;
  if (item.reliability_score_2) {
    const percent = (Number(item.reliability_score_2) / 5) * 100;
    reliability = `${percent}% reliable`;
  }

  const { powers } = item;
  let str = `${(powers[0] / 1000).toFixed(1)} kW`;
  if (powers.length > 1) {
    str += ` - ${(powers[powers.length - 1] / 1000).toFixed(1)} kW`;
  }

  const acceptedCards = chargerInfo?.roaming
    ? chargerInfo.roaming
        .sort((a: Roaming, b: Roaming) => a.emsp.localeCompare(b.emsp))
        .map((e: Roaming) => (
          <li key={e.emsp} className="charger-details__cards-item">
            <span>{e.emsp}</span>
          </li>
        ))
    : process.env.REACT_APP_SHOW_DEBUG
    ? 'Unknown'
    : null;

  const adhocPayment =
    chargerInfo?.adhoc_authorisation_payment_method &&
    chargerInfo?.adhoc_authorisation_payment_method.length
      ? chargerInfo.adhoc_authorisation_payment_method.map((e: AdhocAuthorisationPaymentMethod) => (
          <li key={e.code} className="charger-details__cards-item">
            {e.code}
          </li>
        ))
      : '---';

  const googleLink = encodeURI(`${item.address},${item.postal_code},${item.city}`);

  const infoText = useMemo(
    () => (item.owner.name.toLowerCase() === 'tesla' ? 'tesla_disclaimer' : null),
    [item.owner.name],
  );

  return (
    <div className="charger-details">
      <h3 className="charger-details__title h2">{item.owner.name}</h3>
      <div className="charger-details__info-box">
        <ul className="charger-details__info-list">
          <li className="charger-details__info-item">
            <Icon.Map className="icon-small icon-left" />
            {!atProperty ? (
              <a
                className="link"
                href={`https://www.google.com/maps/dir/?api=1&destination=${googleLink}`}
                target="_blank"
                rel="noreferrer"
              >
                {item.address}, {item.postal_code} {item.city}
              </a>
            ) : (
              'At the property'
            )}
          </li>
          {item.support_phone_number ? (
            <li className="charger-details__info-item">
              <Icon.Phone className="icon-small icon-left" />
              <a href={`tel:${item.support_phone_number}`} className="link">
                {item.support_phone_number}
              </a>
            </li>
          ) : null}
          <li className="charger-details__info-item">
            <Icon.Clock className="icon-small icon-left" />
            {openingTimes}
          </li>
          <li className="charger-details__info-item">
            <Icon.Plug className="icon-small icon-left" />
            {`${item.evses.length} chargers`}
          </li>
          <li className="charger-details__info-item">
            <Icon.Bolt className="icon-small icon-left" />
            {str}
          </li>
          {!reliability ? null : (
            <li className="charger-details__info-item">
              <Icon.Check className="icon-small icon-left" />
              {reliability}
            </li>
          )}
        </ul>
      </div>
      <div className="charger-details__info-group">
        {!chargerInfo ? (
          <div className="charger-details__connector-loader block-loader" />
        ) : !chargerInfo.distinct_connectors ? null : (
          <>
            <h3 className="charger-details__title h3">Plug types</h3>
            <div className="charger-details__connector-container">
              {chargerInfo.distinct_connectors?.map((e: DistinctConnectors) => (
                <ChargerEVSEInfo key={e.id} distinctConnectors={e} chargerInfo={chargerInfo} />
              ))}
            </div>
          </>
        )}
        {!infoText ? null : (
          <div className="charger-details__info-warning">
            <Icon.Alert className="icon-small" />
            <p>{infoText}</p>
          </div>
        )}
      </div>
      {chargerInfo?.predicted_occupancy ? (
        <div className="charger-details__info-group">
          <AvailabilityChart
            checkinWeekday={checkinWeekday}
            occupancy={chargerInfo.predicted_occupancy}
          />
        </div>
      ) : null}
      <div className="charger-details__info-group">
        {!acceptedCards && !process.env.REACT_APP_SHOW_DEBUG ? null : (
          <>
            <h3 className="charger-details__title h3">Payment</h3>
            <h4 className="charger-details__subtitle h4">Cards accepted</h4>
            <ul className="charger-details__cards-list">{acceptedCards}</ul>
            {process.env.REACT_APP_SHOW_DEBUG ? (
              <>
                <h4 className="charger-details__subtitle h4">Ad-hoc payment</h4>
                <ul className="charger-details__cards-list">{adhocPayment}</ul>
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default ChargerDetails;
